import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import useSubscriberLists from '../../hooks/useSubscriberLists';
import { OnlyFansLists } from '../../types/onlyFansTypes';
import { stripHtmlAndConvertParagraphs } from '../../utils/common';
import TextInputDialog from '../common/TextInputDialog';

// ---------- props ----------
export interface ListSingleSelectProps {
    options: OnlyFansLists.List[];
    value: OnlyFansLists.List | null;
    onChange: (next: OnlyFansLists.List | null) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    loading?: boolean;
    /** Parent can react to search text changes (useful for server filtering). */
    onSearchChange?: (text: string) => void;
    /** Title shown in the Card header */
    title?: string;
}

// ---------- virtualized listbox (same infra as your multi) ----------
const LISTBOX_PADDING = 8;

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data as Array<React.ReactElement>;
    const item = dataSet[index];
    return React.cloneElement(item, {
        style: { ...style, top: (style as React.CSSProperties).top },
    });
}

const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef<HTMLDivElement>(function OuterElementType(props, ref) {
    const other = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...other} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
    const { children, style, ...other } = props;
    const itemData = React.Children.toArray(children) as React.ReactElement[];
    const itemCount = itemData.length;
    const ITEM_SIZE = 56;
    const height = Math.min(8, itemCount) * ITEM_SIZE + 2 * LISTBOX_PADDING;

    const outerStyle: React.CSSProperties = { ...(style as React.CSSProperties), overflow: 'hidden', padding: 0 };
    const virtualOuterStyle: React.CSSProperties = { padding: LISTBOX_PADDING };

    return (
        <div ref={ref} {...other} style={outerStyle}>
            <OuterElementContext.Provider value={{ ...other, style: virtualOuterStyle }}>
                <VariableSizeList
                    itemData={itemData}
                    height={height}
                    width="100%"
                    outerElementType={OuterElementType as any}
                    innerElementType="ul"
                    itemSize={() => ITEM_SIZE}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

// ---------- component ----------
export default function ListSingleSelect({
    options,
    value,
    onChange,
    label = 'List',
    placeholder = 'Search lists…',
    size = 'small',
    disabled,
    loading,
    onSearchChange,
    title = 'OnlyFans List',
}: ListSingleSelectProps) {
    const [inputValue, setInputValue] = React.useState('');
    const { refetch: refetchLists, createList, isCreatingList } = useSubscriberLists(true);
    const { enqueueSnackbar } = useSnackbar();

    const [isCreateListDialogOpen, setIsCreateListDialogOpen] = React.useState(false);

    const handleCreateList = (listName: string) => {
        createList(listName)
            .then(newList => {
                if (!newList) {
                    enqueueSnackbar('Failed to create list!', { variant: 'error' });
                    return;
                }

                enqueueSnackbar('List created!', { variant: 'success' });

                onChange(newList);

                setIsCreateListDialogOpen(false);
            })
            .then(() => {
                refetchLists();
            });
    };

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Stack justifyContent="space-between" direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {loading && <CircularProgress size={16} />}
                        <Typography fontWeight={600}>{title}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0} alignItems="center">
                        <Tooltip title="Create new OnlyFans list">
                            <span>
                                <IconButton
                                    disabled={loading || isCreatingList || disabled}
                                    color="success"
                                    onClick={() => {
                                        setIsCreateListDialogOpen(true);
                                    }}
                                >
                                    <AddIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Reload Lists from OnlyFans">
                            <span>
                                <IconButton
                                    disabled={loading}
                                    color="secondary"
                                    onClick={() => {
                                        refetchLists();
                                    }}
                                >
                                    <RefreshIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {loading ? (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <LinearProgress sx={{ flexGrow: 1 }} />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                            {options?.length ? options.length.toLocaleString() : 0}...
                        </Typography>
                    </Stack>
                ) : null}
                {!loading && (
                    <Autocomplete
                        ListboxComponent={ListboxComponent as any}
                        options={options}
                        value={value}
                        size={size}
                        disabled={disabled}
                        loading={loading}
                        inputValue={inputValue}
                        onInputChange={(_, v) => {
                            setInputValue(v);
                            onSearchChange?.(v);
                        }}
                        getOptionLabel={o => o.name}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        renderInput={params => <TextField {...params} label={label} placeholder={placeholder} />}
                        noOptionsText={inputValue ? 'No matching lists' : 'No lists'}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" noWrap fontWeight={600} title={option.name}>
                                            {stripHtmlAndConvertParagraphs(option.name)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.usersCount} users
                                        </Typography>
                                    </Box>
                                </Stack>
                            </li>
                        )}
                        onChange={(_, next) => onChange(next)}
                        // Close the popup after selecting
                        openOnFocus
                        disableClearable={false} // allow clearing via clear button
                        clearOnBlur={false}
                    />
                )}

                {isCreateListDialogOpen && (
                    <TextInputDialog
                        open={isCreateListDialogOpen}
                        onClose={() => setIsCreateListDialogOpen(false)}
                        onSubmit={value => handleCreateList(value.trim())}
                        title="Create New List"
                        contentText="Enter a name for the new OnlyFans list"
                        label="List Name"
                        placeholder="List Name"
                        submitButtonText="Create"
                        isLoading={isCreatingList}
                    />
                )}
            </CardContent>
        </Card>
    );
}
